/* ============================================================================
   DRB strains — a strain opens a panel

   Mike, 2026-09-22, picking off the studies page: "R2, L1 (same as current),
   P2 desktop, P5 mobile." So the row takes a tint on hover, the three-column
   layout is unchanged, and one panel serves both shapes — a two-column slide
   on a laptop, a sheet rising from the bottom edge on a phone.

   ⭐ THE SPECIMEN IS DRAWN, NOT PHOTOGRAPHED, AND THAT IS A DECISION RATHER
   THAN A SHORTCUT. Fifteen strains would be fifteen photographs nobody has,
   of organisms that are genuinely hard to photograph well — and a stock
   microscopy image of the wrong species is worse than an honest drawing. The
   one thing the drawing gets right is real: Lactobacillus and the genera split
   out of it are rods, Bifidobacterium is branched and club-ended. Everything
   else about it is atmosphere.

   ⛔ WHICH MEANS IT MUST NEVER SIT WHERE IT COULD READ AS EVIDENCE. It is an
   illustration of a shape, beside text that will carry citations. If this
   panel ever gains a lab result or lands next to the COA, the drawing comes
   out.
   ============================================================================ */
(function () {
  if (window.__drbStrains) return;
  window.__drbStrains = true;

  /* ⚠️ SEEDED, SO A STRAIN ALWAYS DRAWS THE SAME FIELD. A picture that
     reshuffles every time it is opened reads as decoration; this is standing
     in for a photograph, and a photograph does not change between viewings. */
  function paint(canvas, shape, seed) {
    var ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return;

    /* Match the backing store to the box it is displayed in, or the drawing is
       soft on every phone made since about 2014. */
    var box = canvas.getBoundingClientRect();
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = Math.max(1, Math.round(box.width * dpr));
    var h = Math.max(1, Math.round(box.height * dpr));
    if (!box.width || !box.height) return;
    canvas.width = w;
    canvas.height = h;

    var s = (seed || 1) % 2147483647;
    if (s <= 0) s += 2147483646;
    function rnd() {
      s = (s * 16807) % 2147483647;
      return (s - 1) / 2147483646;
    }

    /* ⛔ THE FIELD IS TINTED PER PRODUCT, NOT HARDCODED GREEN. Mike asked for
       green "so this way we keep it with the green juice kind of look" — right
       for Gut Health, wrong for Women's, which is the red one and whose whole
       page is plum. A green specimen panel there would be the last thing still
       wearing the other product's colour.

       ⚠️ AND THE FIELD CANNOT GO MUCH BRIGHTER THAN THIS. The organisms are
       drawn light with a glow, which is a dark-field microscopy look: the glow
       only reads against a dark ground. The values below lift the field from
       near-black to visibly coloured and stop there. Brighter and the specimens
       flatten out. */
    /* ⭐ THE THIRD TINT IS NOT A PRODUCT COLOUR, AND THAT IS THE POINT.
       /pages/probiotic-strains carries this same section, and that page
       belongs to neither product. Painting it green or plum would hand a
       neutral reference page one product's identity and make it read as an
       offshoot of that PDP rather than the page the term belongs to.
       Mike, 2026-10-03: "instead of making it purple or green, you make it
       black." So neutral is graphite: same dark field, same glow, the
       organisms desaturated rather than hued. */
    var TINTS = {
      green:   { field0: '#21432e', field1: '#0c1e14', glow: 'rgba(150, 240, 190, ', mote: 'rgba(214, 248, 226, ', rod: 158, branch: 150, sat: 52, satNear: 26 },
      plum:    { field0: '#3f1f30', field1: '#1a0a14', glow: 'rgba(240, 170, 205, ', mote: 'rgba(248, 220, 232, ', rod: 338, branch: 330, sat: 52, satNear: 26 },
      neutral: { field0: '#303036', field1: '#0b0b0d', glow: 'rgba(226, 232, 240, ', mote: 'rgba(240, 243, 248, ', rod: 220, branch: 220, sat: 4,  satNear: 8 }
    };
    var ds = (canvas.closest('.drb-strains') || {}).dataset || {};
    var tint = TINTS[ds.specTint] || TINTS.green;

    var g = ctx.createRadialGradient(w * 0.42, h * 0.44, 0, w * 0.5, h * 0.5, Math.max(w, h) * 0.78);
    g.addColorStop(0, tint.field0);
    g.addColorStop(1, tint.field1);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    var branch = shape === 'branch';
    var n = Math.round((w * h) / (5200 * dpr)) + 10;

    for (var i = 0; i < n; i++) {
      var x = rnd() * w;
      var y = rnd() * h;
      /* One number decides size, brightness and blur together, so a body that
         is drawn large is also drawn bright and sharp — which is what depth of
         field actually looks like down a microscope. */
      var near = rnd();
      var len = (branch ? 0.03 : 0.055) * Math.min(w, h) * (0.6 + near * 1.5);
      var thick = len * (branch ? 0.44 : 0.3);

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rnd() * Math.PI);
      ctx.shadowColor = tint.glow + (0.5 * near).toFixed(3) + ')';
      ctx.shadowBlur = len * (0.9 + near);
      ctx.fillStyle =
        /* ⚠️ The organisms shift hue with the field. Mint on plum reads as a
           mistake rather than a choice. */
        'hsla(' + (branch ? tint.branch : tint.rod) + ', ' +
        (tint.sat + near * tint.satNear).toFixed(0) + '%, ' +
        (58 + near * 26).toFixed(0) + '%, ' +
        (0.22 + near * 0.6).toFixed(3) + ')';

      rr(ctx, -len, -thick / 2, len * 2, thick, thick / 2);
      if (branch) {
        /* The fork the genus is named for. */
        ctx.save();
        ctx.translate(len * 0.55, 0);
        ctx.rotate(-0.62);
        rr(ctx, 0, -thick / 2, len * 0.8, thick, thick / 2);
        ctx.restore();
      }
      ctx.restore();
    }

    /* The motes sell the dark field harder than the organisms do. */
    ctx.shadowBlur = 0;
    for (var j = 0; j < Math.round(n / 3); j++) {
      ctx.beginPath();
      ctx.fillStyle = tint.mote + (0.1 + rnd() * 0.3).toFixed(3) + ')';
      ctx.arc(rnd() * w, rnd() * h, rnd() * Math.min(w, h) * 0.008 + 0.6, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  function rr(ctx, x, y, w, h, r) {
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(x, y, w, h, r);
    else ctx.rect(x, y, w, h);
    ctx.fill();
  }

  function seedOf(text) {
    var n = 0;
    for (var i = 0; i < text.length; i++) n = (n * 31 + text.charCodeAt(i)) % 2147483647;
    return n || 7;
  }

  document.addEventListener('click', function (event) {
    var closer = event.target.closest('[data-drb-strain-close]');
    if (closer) {
      var own = closer.closest('dialog');
      if (own) own.close();
      return;
    }

    /* The dialog fills the viewport while its own box does not, so a click
       that lands ON it is a backdrop click. */
    var back = event.target.closest('[data-drb-strain-panel]');
    if (back && event.target === back) {
      back.close();
      return;
    }

    var hit = event.target.closest('[data-drb-strain]');
    if (!hit) return;

    var section = hit.closest('.drb-strains');
    var panel = section && section.querySelector('[data-drb-strain-panel]');
    if (!panel || typeof panel.showModal !== 'function') return;

    var title = panel.querySelector('[data-drb-strain-title]');
    var fields = panel.querySelector('[data-drb-strain-fields]');
    var name = hit.querySelector('.drb-strains__nm');

    /* ⚠️ NODES ARE CLONED, NEVER SERIALISED THROUGH innerHTML. The strain line
       is merchant rich text with its own italics, and the fields will carry
       citation links; cloning keeps both intact and keeps merchant content out
       of a string that gets re-parsed as markup. */
    if (title && name) {
      title.textContent = '';
      title.appendChild(name.cloneNode(true));
    }

    if (fields) {
      fields.textContent = '';
      var store = hit.parentNode.querySelector('template');
      if (store && store.content) fields.appendChild(store.content.cloneNode(true));
    }

    var canvas = panel.querySelector('[data-drb-strain-canvas]');
    panel.showModal();
    /* After showModal, because a dialog has no measurable box until it is
       open — painting first gives a zero-width canvas and a blank frame. */
    if (canvas) {
      paint(canvas, hit.getAttribute('data-shape'), seedOf(name ? name.textContent : 'x'));
    }
  });

  /* ══════════════ the affordance hint ══════════════

     Mike, 2026-09-22: the strains look like a list, not like fifteen buttons.
     One row wears the hover state with a callout so the reader learns the whole
     column is clickable, then it gets out of the way.

     ⛔ IT DISMISSES ON THE FIRST SIGN OF LIFE, PER THE SITE-WIDE RULE — "if you
     mouse over it or click it, it should stop". A hint is a one-shot, so
     "stop" means gone for the rest of the visit rather than paused: once
     someone has moved a pointer onto the list, tabbed into it, or touched it,
     they have discovered the thing the hint exists to teach, and leaving it up
     is nagging.

     ⚠️ AND IT NEVER WAITS FOR A pointerleave, WHICH IS THE TOUCH TRAP THIS
     PROJECT HAS ALREADY PAID FOR ONCE. A finger landing fires pointerenter; if
     the touch becomes a scroll the browser takes the gesture and fires
     pointercancel, and pointerleave NEVER ARRIVES. Anything holding state
     across that pair sticks forever — it killed the doctors row. Here the
     interaction only ever REMOVES the hint, so a cancelled gesture costs
     nothing. */
  /* Mike, 2026-09-22: "it should hold for 6 seconds then off for 3 seconds then
     if someone doesnt click but remains in that area it starts again." */
  /* Mike, 2026-09-30: "every time I get there, it's just not that exciting.
     It's boring... it probably needs to show every time you mouse over that area
     too and show for like two, three seconds, or maybe it should go between two
     or three different ones on the page, just like slide up or down so you can
     see that you can select different ones."

     ⭐ SO THE HINT NOW DEMONSTRATES THE INTERACTION INSTEAD OF DESCRIBING IT.
     The old version parked a bubble reading "Click for more" on one row for six
     seconds. That is a caption. Walking the highlight down three consecutive
     strains shows the reader three things a caption cannot: these rows respond,
     there is more than one of them, and you can move between them. The motion
     is the message.

     ⚠️ THE BUBBLE ONLY RIDES THE FIRST ROW OF EACH PASS. Words explain once;
     the walk does the rest. Repeating the same callout on all three rows reads
     as nagging and adds no information. LABEL_LEAD_ONLY flips it in one line if
     Mike wants it louder. */
  /* ⭐ MIKE ASKED FOR THE WHOLE LIST, 2026-09-30: "you might as well just have
     it go through all of them... just go through the whole list, one by one by
     one by one, until you click and then it stops."

     He is right, and the reason it works is the same reason three rows beat a
     caption: the walk is a tour, not a hint. Stopping at three taught that the
     first three respond; walking all fifteen shows the whole column is live and
     puts every strain in front of someone who was never going to tap through
     them one at a time. At 2.2s a step that is a 33-second pass, which is
     ambient rather than insistent, and any real interaction still ends it. */
  var HINT_STEP = 2200;
  var HINT_REST = 4000;
  var HINT_ROWS = 0; /* 0 = every strain in the section */
  var LABEL_LEAD_ONLY = true;

  function armHint(section) {
    var rows = [].slice.call(section.querySelectorAll('[data-drb-strain]'));
    if (HINT_ROWS > 0) rows = rows.slice(0, HINT_ROWS);
    if (!rows.length) return;

    var timer = null;
    var i = 0;
    var stopped = false;
    var inView = false;
    var onRow = false;

    var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function clear() {
      if (timer) clearTimeout(timer);
      timer = null;
    }

    function blank() {
      rows.forEach(function (r) {
        r.classList.remove('is-hint', 'is-hint--lead');
      });
    }

    function paint(n) {
      rows.forEach(function (r, k) {
        r.classList.toggle('is-hint', k === n);
        r.classList.toggle('is-hint--lead', k === n && (!LABEL_LEAD_ONLY || n === 0));
      });
    }

    /* One tick of the walk: rows 0,1,2 in turn, then a blank rest, then again.
       Every reason not to run is checked HERE rather than at the call sites —
       the observer, the area-hover restart and the row-hover resume all land on
       this one function, and a call site that decided for itself would restart a
       cycle someone is in the middle of using. */
    function step() {
      if (stopped || !inView || onRow) return;
      if (i >= rows.length) {
        blank();
        i = 0;
        timer = setTimeout(step, HINT_REST);
        return;
      }
      paint(i);
      /* ⛔ prefers-reduced-motion gets ONE static showing, not the walk. Someone
         who asked the system to stop things moving has not asked to be denied
         the information that a row is clickable. */
      if (still) return;
      i += 1;
      timer = setTimeout(step, HINT_STEP);
    }

    function restart() {
      if (stopped || !inView || onRow) return;
      clear();
      i = 0;
      step();
    }

    /* ⛔ A CLICK ON A STRAIN HOLDS IT PERMANENTLY, HOVER DOES NOT. Per the table
       in CLAUDE.md: a click that means something is a real choice and there is
       nothing to recover from, so the hint has no job left. Hovering is not a
       choice — the reader may still be deciding — which is why Mike now wants
       the hint to come back rather than die on first contact.

       ⚠️ AND THE OLD touchstart-STOP IS GONE ON PURPOSE. It killed the hint for
       the rest of the visit the moment a finger touched the section, including
       a finger that was only starting a scroll. On a phone that meant most
       visitors saw the affordance once, briefly, or never. A tap that actually
       opens a strain still stops it, because that fires click. */
    function stop() {
      if (stopped) return;
      stopped = true;
      clear();
      blank();
    }

    section.addEventListener('click', function (e) {
      if (e.target.closest('[data-drb-strain]')) stop();
    });
    section.addEventListener('focusin', function (e) {
      if (e.target.closest('[data-drb-strain]')) stop();
    });

    /* ⛔ A FINGER DOES NOT HOVER. On a touch screen pointerenter fires the moment
       a finger lands, and if the touch becomes a scroll the browser takes the
       gesture and fires pointercancel — pointerleave NEVER ARRIVES. Anything
       holding state across that pair sticks forever; it killed the doctors row
       once already. Touch pointers are ignored outright here, and pointercancel
       clears the flag if one slips through. */
    section.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'touch') return;
      restart();
    });

    /* pointerover BUBBLES, which pointerenter does not — so this one listener
       can tell "pointer is on a strain" from "pointer is elsewhere in the
       section" without a listener per row. */
    section.addEventListener('pointerover', function (e) {
      if (e.pointerType === 'touch') return;
      if (e.target.closest('[data-drb-strain]')) {
        if (onRow) return;
        onRow = true;
        clear();
        blank();
      } else if (onRow) {
        onRow = false;
        restart();
      }
    });

    section.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'touch') return;
      onRow = false;
      clear();
      blank();
    });

    section.addEventListener('pointercancel', function () {
      onRow = false;
    });

    /* ⚠️ Fires when the section is actually looked at, not on load, and is NOT
       disconnected — the walk has to follow the reader. Scrolling away stops it
       mid-pass and scrolling back starts it over, which also stops it burning
       its cycle against an empty screen. */
    if (!('IntersectionObserver' in window)) {
      inView = true;
      return step();
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (stopped) return;
          inView = entry.isIntersecting;
          if (inView) {
            if (!timer) restart();
          } else {
            clear();
            blank();
          }
        });
      },
      { threshold: 0.35 }
    );

    io.observe(section);
  }

  document.querySelectorAll('.drb-strains').forEach(armHint);
})();
